export interface Throttler<T> {
    schedule(value: T): void
    flush(): void
    cancel(): void
}

/**
 * Leading + trailing throttler: the first value is sent immediately (when the
 * interval since the last send has elapsed), values arriving within the
 * interval are coalesced into a single trailing send of the latest value.
 */
export const createThrottler = <T>(onSend: (value: T) => void, intervalMs = 1000): Throttler<T> => {
    let lastSentAt = -Infinity
    let pendingValue: T | null = null
    let timer: ReturnType<typeof setTimeout> | null = null

    const sendNow = (value: T) => {
        lastSentAt = Date.now()
        pendingValue = null
        onSend(value)
    }

    const fireTrailing = () => {
        timer = null
        if (pendingValue !== null) {
            sendNow(pendingValue)
        }
    }

    const schedule = (value: T) => {
        const elapsed = Date.now() - lastSentAt
        if (timer === null && elapsed >= intervalMs) {
            sendNow(value)
            return
        }
        pendingValue = value
        if (timer === null) {
            timer = setTimeout(fireTrailing, Math.max(0, intervalMs - elapsed))
        }
    }

    const flush = () => {
        if (timer !== null) {
            clearTimeout(timer)
            timer = null
        }
        if (pendingValue !== null) {
            sendNow(pendingValue)
        }
    }

    const cancel = () => {
        if (timer !== null) {
            clearTimeout(timer)
            timer = null
        }
        pendingValue = null
    }

    return {schedule, flush, cancel}
}
